import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { forwardRef, useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { OVERLAY_SCRIM, applyInitialFocus, type OverlayBaseProps } from './Dialog';

export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

/** Widths used by the Admin Console drawers: 400 · 520 (add module) · 640 (AI generate) · 720 (history). */
const DRAWER_WIDTH: Record<DrawerSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[640px]',
  xl: 'max-w-[720px]',
};

export interface DrawerProps extends OverlayBaseProps {
  title: ReactNode;
  /** Muted line under the title, e.g. "Acme ERP · STAGING". */
  subtitle?: ReactNode;
  children?: ReactNode;
  /** Footer actions (right-aligned, bordered top). */
  footer?: ReactNode;
  size?: DrawerSize;
  /** Custom max-width (overrides `size`), e.g. '80%'. */
  width?: number | string;
  hideClose?: boolean;
  dismissible?: boolean;
  labels?: Partial<{ close: string }>;
  className?: string;
  bodyClassName?: string;
}

export const DrawerClose = RadixDialog.Close;

/**
 * Side sheet from the inline-end edge (right in LTR, left in RTL): role="dialog" aria-modal,
 * header / scrolling body / footer, focus to the title on open and back to the trigger on close.
 * The slide animation is disabled under prefers-reduced-motion.
 */
export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    open,
    defaultOpen,
    onOpenChange,
    trigger,
    title,
    subtitle,
    children,
    footer,
    size = 'md',
    width,
    hideClose = false,
    initialFocus = 'title',
    dismissible = true,
    modal = true,
    container,
    labels,
    className,
    bodyClassName,
  },
  ref,
) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const l = { close: 'Close', ...labels };
  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal container={container}>
        {modal ? <RadixDialog.Overlay className={OVERLAY_SCRIM} /> : null}
        <RadixDialog.Content
          ref={ref}
          aria-modal={modal || undefined}
          {...(subtitle ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={(event) => applyInitialFocus(initialFocus, titleRef, event)}
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (!modal) event.preventDefault();
          }}
          style={width !== undefined ? { maxWidth: width } : undefined}
          className={cn(
            'fixed inset-y-0 end-0 z-50 flex w-full flex-col overflow-hidden rounded-s-lg bg-surface text-text shadow-lg outline-none',
            'data-[state=open]:animate-drawer-in data-[state=closed]:animate-drawer-out',
            width === undefined && DRAWER_WIDTH[size],
            className,
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <RadixDialog.Title
                ref={titleRef}
                tabIndex={-1}
                className="truncate text-16 font-semibold leading-6 outline-none focus-visible:shadow-none"
              >
                {title}
              </RadixDialog.Title>
              {subtitle ? (
                <RadixDialog.Description className="text-12 text-text-2">{subtitle}</RadixDialog.Description>
              ) : null}
            </div>
            {hideClose ? null : (
              <RadixDialog.Close
                aria-label={l.close}
                className="grid size-[30px] shrink-0 place-items-center rounded-[6px] text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
              >
                <X aria-hidden className="size-4" strokeWidth={2} />
              </RadixDialog.Close>
            )}
          </div>
          <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-5 text-13', bodyClassName)}>
            {children}
          </div>
          {footer ? (
            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-5 py-3.5">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});
